import { useMemo, useState } from "react";
import { Globe, Calendar, Clock } from "lucide-react";
import { TIMEZONES } from "../data";
import {
  generateAvailability,
  formatDateLabel,
  formatTime,
  tzAbbr,
} from "../lib/scheduling";

export default function DateTimePicker({ timeZone, setTimeZone, value, onChange }) {
  const days = useMemo(() => generateAvailability(28), []);
  const [activeDay, setActiveDay] = useState(0);

  if (!days.length) {
    return (
      <div className="text-sm text-[#A3A8B8] bg-[#131E3D] border border-[#1D2847] px-4 py-3">
        No open slots in the next few weeks — submit the form and we'll reach out to schedule.
      </div>
    );
  }

  const day = days[Math.min(activeDay, days.length - 1)];
  const abbr = tzAbbr(timeZone);

  return (
    <div className="space-y-4">
      {/* Timezone */}
      <label className="block">
        <span className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#A3A8B8] font-bold mb-2">
          <Globe size={13} /> Your Timezone
        </span>
        <select
          data-testid="booking-timezone"
          value={timeZone}
          onChange={(e) => setTimeZone(e.target.value)}
          className="w-full bg-[#131E3D] border border-[#1D2847] text-[#F8F9FA] focus:border-[#D4AF37] focus:outline-none px-4 py-3 appearance-none"
        >
          {TIMEZONES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </label>

      {/* Date chips */}
      <div>
        <span className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#A3A8B8] font-bold mb-2">
          <Calendar size={13} /> Choose a Day
        </span>
        <div className="flex gap-2 overflow-x-auto pb-2" data-testid="booking-date-list">
          {days.map((d, i) => (
            <button
              key={`${d.year}-${d.month}-${d.day}`}
              type="button"
              data-testid={`booking-date-${i}`}
              onClick={() => setActiveDay(i)}
              className={`shrink-0 px-4 py-2 text-sm font-semibold border transition-colors ${
                i === activeDay
                  ? "bg-[#D4AF37] text-[#050A15] border-[#D4AF37]"
                  : "bg-[#131E3D] text-[#F8F9FA] border-[#1D2847] hover:border-[#D4AF37]/50"
              }`}
            >
              {formatDateLabel(d.year, d.month, d.day, timeZone)}
            </button>
          ))}
        </div>
      </div>

      {/* Time chips */}
      <div>
        <span className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#A3A8B8] font-bold mb-2">
          <Clock size={13} /> Choose a Time ({abbr})
        </span>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="booking-time-list">
          {day.slots.map((iso) => (
            <button
              key={iso}
              type="button"
              data-testid={`booking-time-${iso}`}
              onClick={() => onChange(iso)}
              className={`px-2 py-2 text-sm font-semibold border transition-colors ${
                value === iso
                  ? "bg-[#D4AF37] text-[#050A15] border-[#D4AF37]"
                  : "bg-[#131E3D] text-[#F8F9FA] border-[#1D2847] hover:border-[#D4AF37]/50"
              }`}
            >
              {formatTime(iso, timeZone)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
